import {
    useCallback,
    KeyboardEventHandler,
    useRef,
    ReactNode,
    useMemo,
    useLayoutEffect,
    useState,
    forwardRef,
    ForwardedRef,
    ReactElement,
} from 'react';
import classnames from 'classnames';

import { throttle } from '@hh.ru/magritte-common-func-utils';
import { keyboardKeys, keyboardMatches } from '@hh.ru/magritte-common-keyboard';
import { useResize } from '@hh.ru/magritte-common-resize';
import { useMultipleRefs } from '@hh.ru/magritte-common-use-multiple-refs';
import { Action } from '@hh.ru/magritte-ui-action';
import { FormHelper } from '@hh.ru/magritte-ui-form-helper';
import {
    ChevronDownOutlinedSize16,
    ChevronDownOutlinedSize24,
    CrossOutlinedSize16,
    CrossOutlinedSize24,
} from '@hh.ru/magritte-ui-icon/icon';
import { type IconWrapperComponentSize24, type IconWrapperComponentSize16 } from '@hh.ru/magritte-ui-icon/iconTypes';
import { TriggerProps, TriggerSize } from '@hh.ru/magritte-ui-trigger/types';

import styles from './trigger.less';

// расчитываем размер шеврона на базе `size`
// если состояние `applied`, то нужно использовать крест
const getIcon = (size: TriggerSize, applied: boolean): IconWrapperComponentSize24 | IconWrapperComponentSize16 => {
    if (size === 'large' || size === 'medium') {
        if (applied) {
            return CrossOutlinedSize24;
        }
        return ChevronDownOutlinedSize24;
    }
    if (applied) {
        return CrossOutlinedSize16;
    }
    return ChevronDownOutlinedSize16;
};

const defaultRenderFormattedValue = (value: ReactNode, index: number, length: number) => {
    return (
        <>
            {value}
            {index !== length - 1 && ', '}
        </>
    );
};

export const TriggerComponent = (
    {
        applied,
        description,
        disabled,
        expanded,
        maxWidth,
        invalid,
        values = [],
        label,
        elevateLabel,
        errorMessage,
        renderFormattedValue = defaultRenderFormattedValue,
        trls,
        stretched,
        size,
        showPostfix,
        onChange,
        onClear,
        onFocus,
        onKeyDown,
        onBlur,
        'data-qa': dataQa,
    }: TriggerProps,
    ref: ForwardedRef<HTMLElement>
): ReactElement => {
    const triggerRef = useRef<HTMLDivElement>(null);
    const triggerRefCallback = useMultipleRefs(ref, triggerRef);
    const itemsRefs = useRef<HTMLElement[]>([]);
    const itemsWrapperRef = useRef<HTMLDivElement>(null);
    const postfixMetricRef = useRef<HTMLDivElement>(null);
    const showValues = values.length > 0;
    const labelCanBeElevated = size === 'large' && !!elevateLabel && !!label;
    const labelIsElevated = showValues && labelCanBeElevated;
    const IconComponent = getIcon(size, Boolean(applied));
    const [postfix, setPostfix] = useState<number>(0);
    const postfixRef = useRef<number>(postfix);
    postfixRef.current = postfix;
    const formattedValues = useMemo(() => {
        itemsRefs.current = [];
        return values.map((value, index) => (
            <span key={index} ref={(element) => element && (itemsRefs.current[index] = element)}>
                {renderFormattedValue(value, index, values.length)}
            </span>
        ));
    }, [renderFormattedValue, values]);

    const handleClickTrigger = useCallback(() => {
        onChange(!expanded);
    }, [onChange, expanded]);

    const handleKeyDownTrigger = useCallback<KeyboardEventHandler>(
        (event) => {
            if (keyboardMatches(event.nativeEvent, [keyboardKeys.Space])) {
                onChange(!expanded);
            }
            onKeyDown?.(event);
        },
        [onChange, onKeyDown, expanded]
    );

    const handleClickClear = useCallback(() => {
        onClear?.();
        triggerRef.current?.focus();
    }, [onClear]);

    const handleKeyDownClear = useCallback<KeyboardEventHandler>(
        (event) => {
            if (keyboardMatches(event.nativeEvent, [keyboardKeys.Space])) {
                onClear?.();
                triggerRef.current?.focus();
                event.stopPropagation();
            }
        },
        [onClear]
    );

    // eslint-disable-next-line disable-autofix/react-hooks/exhaustive-deps
    const calcPostfix = useCallback(
        throttle(() => {
            if (!itemsWrapperRef.current || !postfixMetricRef.current) {
                return;
            }
            if (!showPostfix) {
                return;
            }
            // перевод в постфиксе может поменяться, а ширину надо учесть
            postfixMetricRef.current.style.display = 'inline';
            const postfixRect = postfixMetricRef.current.getBoundingClientRect();
            const postfixWidth = postfixRect.width;
            postfixMetricRef.current.style.display = 'none';
            const parentRect = itemsWrapperRef.current.getBoundingClientRect();
            const parentWidth = parentRect.width;
            // если постфикс виден, то ширину его не учитываем (реф стоит на values)
            const parentWidthWithoutPostfix = parentWidth - (postfixRef.current === 0 ? postfixWidth : 0);
            // ищем первый элемент, где значения схлопываются в ...
            let truncatedItemIndex = -1;
            let childrenWidth = 0;
            itemsRefs.current.forEach((itemRef, index) => {
                const itemRect = itemRef.getBoundingClientRect();
                childrenWidth += itemRect.width;
                if (truncatedItemIndex === -1 && childrenWidth > parentWidthWithoutPostfix) {
                    truncatedItemIndex = index;
                }
            });
            // проверяем, есть ли ... в тригере
            if (Math.abs(parentWidth - childrenWidth) < 1) {
                setPostfix(0);
                return;
            }
            const length = values.length - 1;
            if (truncatedItemIndex === -1 || truncatedItemIndex === length) {
                setPostfix(0);
                return;
            }
            setPostfix(length - truncatedItemIndex);
        }, 50),
        [values, showPostfix, maxWidth]
    );

    useLayoutEffect(calcPostfix, [calcPostfix]);
    useResize(calcPostfix);

    return (
        <div
            className={classnames(styles.triggerRoot, { [styles.triggerRootStretched]: stretched })}
            style={maxWidth ? { maxWidth } : {}}
        >
            <div
                ref={triggerRefCallback}
                className={classnames(styles.trigger, styles[size], {
                    [styles.applied]: applied,
                    [styles.disabled]: disabled,
                    [styles.invalid]: invalid,
                    [styles.selected]: showValues,
                })}
                onClick={!disabled ? handleClickTrigger : undefined}
                onKeyDown={!disabled ? handleKeyDownTrigger : undefined}
                onFocus={onFocus}
                onBlur={onBlur}
                tabIndex={disabled ? -1 : 0}
                data-qa={dataQa || 'trigger-root'}
            >
                <div
                    className={classnames(styles.content, styles[`content_${size}`], {
                        [styles.contentLabelCanBeElevated]: labelCanBeElevated,
                    })}
                >
                    {labelCanBeElevated && (
                        <div
                            className={classnames(styles.label, {
                                [styles.labelElevated]: labelIsElevated,
                            })}
                        >
                            {label}
                        </div>
                    )}
                    {!labelCanBeElevated && !showValues && <div className={styles.label}>{label}</div>}
                    {showValues && (
                        <div
                            className={classnames(styles.valuesWrapper, {
                                [styles.valuesWrapperLabelCanElevated]: labelIsElevated,
                            })}
                        >
                            <div ref={itemsWrapperRef} className={styles.values} data-qa="trigger-values-wrapper">
                                {formattedValues}
                            </div>
                            {showPostfix && postfix > 0 && (
                                <div
                                    className={styles.postfix}
                                    data-qa="trigger-postfix"
                                >{`${trls?.postfix} ${postfix}`}</div>
                            )}
                        </div>
                    )}
                    {showPostfix && (
                        <span
                            ref={postfixMetricRef}
                            className={classnames(styles.postfix, styles.postfixMetric)}
                            data-qa="trigger-postfix-metric"
                        >{`${trls?.postfix} ${postfix || 1}`}</span>
                    )}
                </div>
                {!applied && (
                    <div
                        className={classnames(styles.icon, {
                            [styles.iconExpanded]: expanded,
                        })}
                    >
                        <IconComponent />
                    </div>
                )}
                {applied && (
                    <div className={styles.icon}>
                        <Action
                            onKeyDown={handleKeyDownClear}
                            onClick={handleClickClear}
                            icon={IconComponent}
                            disabled={disabled}
                            disablePadding
                        />
                    </div>
                )}
            </div>
            <FormHelper description={description} errorMessage={errorMessage} disabled={disabled} invalid={invalid} />
        </div>
    );
};

export const Trigger = forwardRef(TriggerComponent) as (
    props: TriggerProps & { ref?: ForwardedRef<HTMLElement> }
) => ReactElement;
